import React, { useCallback, useMemo, useState } from 'react';
import { Formik, Form, Field, FormikHelpers, FieldProps, ErrorMessage } from 'formik';
import Modal, {
    ModalContent,
    ModalFooter,
    ModalProps,
    ModalTitle,
} from '../../../../../../../ui/Layout/Modals/Modal/Modal';
import Button from '../../../../../../../ui/Buttons/Button/Button';
import FormField from '../../../../../../../ui/DataEntry/FormField/FormField';
import Label from '../../../../../../../ui/DataEntry/Label/Label';
import { showNotification } from '../../../../../../../helpers';
import { GenericDropdownOption } from '../../../../../../../App.types';
import Select from '../../../../../../../ui/DataEntry/Select/Select';
import { StylesConfig, ValueType } from 'react-select';
import styles from './AddNewSongModal.module.css';
import { ClientSimple, postClientMonitoredTiktokAudioRelationship } from '@round/api';

type AddNewSongModalProps = ModalProps & { clients: ClientSimple[] };
type FormValues = {
    url: string;
    custom_name: string;
    client_id: ValueType<GenericDropdownOption<number>, false>;
};

const initialValues: FormValues = {
    url: '',
    custom_name: '',
    client_id: null,
};

const selectStyles: StylesConfig = {
    control: (provided) => ({
        ...provided,
        borderColor: '#c2cfe0',
        fontSize: '0.8125rem',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#90a0b7',
    }),
    input: (provided) => ({
        ...provided,
        height: 'auto',
    }),
};

const AddNewSongModal = ({ isOpen, onClose, closeOnOverlayClick = true, clients }: AddNewSongModalProps) => {
    const [showSuccessText, setShowSuccessText] = useState(false);
    const clientOptions: GenericDropdownOption<number>[] = useMemo(
        () =>
            clients.map((client) => ({
                value: client.id,
                label: client.name,
            })),
        [clients]
    );

    const onSubmit = useCallback(
        async (values: FormValues, { setSubmitting, setErrors, resetForm }: FormikHelpers<FormValues>) => {
            if (!values.client_id) {
                return;
            }

            try {
                setSubmitting(true);
                const response = await postClientMonitoredTiktokAudioRelationship({
                    url: values.url,
                    client_id: values.client_id.value,
                    custom_name: values.custom_name,
                });

                if (response.status === 201) {
                    setShowSuccessText(true);
                    resetForm();
                    return;
                }

                setErrors(response.data);
            } catch (e) {
                setErrors({ url: (e as Error).message });
                showNotification('Could not add song', 'error');
            } finally {
                setSubmitting(false);
            }
        },
        []
    );

    return (
        <Modal
            isOpen={isOpen}
            closeOnOverlayClick={closeOnOverlayClick}
            onClose={() => {
                if (typeof onClose === 'function') {
                    setShowSuccessText(false);
                    onClose();
                }
            }}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit}>
                {({ isSubmitting, submitForm, dirty }) => (
                    <>
                        <ModalTitle>Create Song</ModalTitle>
                        <ModalContent>
                            <Form className={styles.form}>
                                <FormField>
                                    <Label htmlFor="client_id">Client</Label>
                                    <Field name="client_id" id="client_id">
                                        {(props: FieldProps) => (
                                            <Select
                                                styles={selectStyles}
                                                placeholder="Select client"
                                                options={clientOptions}
                                                value={props.field.value}
                                                onChange={(value) => props.form.setFieldValue('client_id', value)}
                                            />
                                        )}
                                    </Field>
                                    <ErrorMessage name="client_id">
                                        {(message) => <span className={styles.errorMessage}>{message}</span>}
                                    </ErrorMessage>
                                </FormField>

                                <FormField>
                                    <Label htmlFor="url">URL</Label>
                                    <Field name="url" id="url" placeholder="Enter URL" />
                                    <ErrorMessage name="url">
                                        {(message) => <span className={styles.errorMessage}>{message}</span>}
                                    </ErrorMessage>
                                </FormField>

                                <FormField>
                                    <Label htmlFor="custom_name">Custom name (for original sounds)</Label>
                                    <Field name="custom_name" id="custom_name" placeholder="Enter custom name" />
                                </FormField>
                            </Form>
                        </ModalContent>
                        <ModalFooter className={styles.footer}>
                            {showSuccessText && !dirty && (
                                <div className={styles.successMessageContainer}>
                                    <p className={styles.successText}>Successfully added!</p>
                                    <p>Data may take up to an hour to display</p>
                                </div>
                            )}
                            <Button type="filled" color="black" loading={isSubmitting} onClick={submitForm}>
                                Add
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </Formik>
        </Modal>
    );
};

export default AddNewSongModal;
